// Small pieces every page uses: links that route in the page, logos, chain marks, changes, labels.
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { CHAINS, LAUNCHPADS, type ChainId, type Socials } from "./api";
import robinhood from "./chains/robinhood.png";
import { Icon, type IconName } from "./icons";
import fourmeme from "./launchpads/fourmeme.png";
import letsbonk from "./launchpads/letsbonk.png";
import meteoraDbc from "./launchpads/meteora-dbc.png";
import pumpfun from "./launchpads/pumpfun.png";
import raydiumLaunchlab from "./launchpads/raydium-launchlab.png";
import { pct } from "./format";

// ─── Routing: the path is the page ───

export function go(href: string) {
  history.pushState(null, "", href);
  dispatchEvent(new PopStateEvent("popstate"));
  scrollTo(0, 0);
}

export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const f = () => setPath(location.pathname);
    addEventListener("popstate", f);
    return () => removeEventListener("popstate", f);
  }, []);
  return path;
}

/** A link inside the app: a plain click routes in the page, a modified one opens a tab as usual. */
export function A({ href, children, className, title }: { href: string; children: ReactNode; className?: string; title?: string }) {
  const click = (e: MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    go(href);
  };
  return (
    <a href={href} onClick={click} className={className} title={title}>
      {children}
    </a>
  );
}

export function Out({ href, children, className, title }: { href: string; children: ReactNode; className?: string; title?: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} title={title} onClick={(e) => e.stopPropagation()}>
      {children}
    </a>
  );
}

// ─── Marks ───

export function LogoMark({ size = 26 }: { size?: number }) {
  return (
    <svg height={size} width={(size * 1201) / 1382} viewBox="0 0 1201 1382" aria-hidden="true" fill="currentColor">
      <path fillRule="evenodd" d={MARK} />
    </svg>
  );
}

export function ChainMark({ chain, size = 14 }: { chain: ChainId; size?: number }) {
  return (
    <span className="chain-mark" style={{ width: size, height: size }} title={CHAINS[chain].name}>
      {chain === "solana" ? (
        <svg viewBox="0 0 397.7 311.7" aria-hidden="true">
          <defs>
            <linearGradient id="sol-g" gradientUnits="userSpaceOnUse" x1="360.9" y1="-37.5" x2="141.2" y2="383.3">
              <stop offset="0" stopColor="#14F195" />
              <stop offset="1" stopColor="#9945FF" />
            </linearGradient>
          </defs>
          <g fill="url(#sol-g)">
            <path d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7z" />
            <path d="M64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8z" />
            <path d="M333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.7z" />
          </g>
        </svg>
      ) : chain === "evm:8453" ? (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect width="16" height="16" rx="3" fill="#0052FF" />
        </svg>
      ) : chain === "evm:1" ? (
        <svg viewBox="0 0 256 417" aria-hidden="true">
          <g fill="#627EEA">
            <path d="M127.96 0 125.17 9.5v275.67l2.79 2.79 127.96-75.64z" />
            <path d="M127.96 0 0 212.32l127.96 75.64V154.16z" opacity=".7" />
            <path d="m127.96 312.19-1.58 1.92v98.2l1.58 4.6L256 236.59z" />
            <path d="M127.96 416.91V312.19L0 236.59z" opacity=".7" />
          </g>
        </svg>
      ) : chain === "evm:4663" ? (
        <img src={robinhood} alt="" />
      ) : (
        <svg viewBox="0 0 126.61 126.61" aria-hidden="true">
          <g fill="#F0B90B">
            <path d="m38.73 53.2 24.59-24.58 24.6 24.6 14.3-14.31-38.9-38.91-38.9 38.9z" />
            <path d="m0 63.31 14.3-14.31 14.31 14.31-14.31 14.3z" />
            <path d="m38.73 73.41 24.59 24.59 24.6-24.6 14.31 14.29-38.9 38.91-38.91-38.88-.02-.02z" />
            <path d="m98 63.31 14.3-14.31 14.31 14.3-14.31 14.32z" />
            <path d="m77.83 63.3-14.51-14.52-10.73 10.73-1.24 1.23-2.54 2.54 14.51 14.5 14.51-14.47z" />
          </g>
        </svg>
      )}
    </span>
  );
}

// Each launchpad's own mark, from its site or token (pump.fun's is the PUMP token's), as 64px PNGs.
const LAUNCHPAD_LOGOS: Record<string, string> = {
  pumpfun,
  letsbonk,
  "raydium-launchlab": raydiumLaunchlab,
  "meteora-dbc": meteoraDbc,
  fourmeme,
};

/** A launchpad's mark, and its name unless `bare`; the name alone where Aurelia has no mark. */
export function Launchpad({ id, size = 14, bare }: { id: string; size?: number; bare?: boolean }) {
  const name = LAUNCHPADS[id] ?? id;
  const src = LAUNCHPAD_LOGOS[id];
  return (
    <span className="launchpad" title={bare ? name : undefined}>
      {src && <img src={src} alt={bare ? name : ""} width={size} height={size} className="launchpad-mark" />}
      {(!bare || !src) && <span>{name}</span>}
    </span>
  );
}

/** A token's logo with its chain in the corner; its symbol's first letter while there is none. */
export function TokenLogo({ src, symbol, chain, size = 28 }: { src: string | null; symbol: string | null; chain?: ChainId; size?: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="token-logo" style={{ width: size, height: size }}>
      {src && !failed ? (
        <img src={src} alt="" width={size} height={size} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span className="token-logo-letter" style={{ fontSize: size * 0.42 }}>
          {(symbol ?? "?").replace(/^\$/, "").slice(0, 1).toUpperCase()}
        </span>
      )}
      {chain && (
        <span className="token-logo-chain">
          <ChainMark chain={chain} size={Math.max(10, size * 0.4)} />
        </span>
      )}
    </span>
  );
}

// ─── Values ───

export function Change({ value }: { value: number | null | undefined }) {
  // Under 0.005% it shows as 0.00%: neither green nor red, and no sign.
  const v = value != null && Math.abs(value) < 0.00005 ? 0 : value;
  const cls = v == null || v === 0 ? "flat" : v > 0 ? "up" : "down";
  return <span className={`num ${cls}`}>{pct(v)}</span>;
}

// What a wallet is known to be, and a holder's tags, as an icon; the word on hover.
const LABELS: Record<string, { icon: IconName; name: string }> = {
  creator: { icon: "dev", name: "Dev: created this token" },
  insider: { icon: "insider", name: "Insider" },
  sniper: { icon: "sniper", name: "Sniper" },
  smart_money: { icon: "smart", name: "Smart money" },
  bot: { icon: "bot", name: "Bot" },
  fresh: { icon: "fresh", name: "Fresh wallet" },
  burn: { icon: "burn", name: "Burn address" },
  pool: { icon: "pool", name: "A pool of this token" },
};
export function Labels({ labels }: { labels: string[] }) {
  if (!labels.length) return null;
  return (
    <span className="labels">
      {labels.map((l) => (
        <span key={l} className={`label label-${l}`} title={LABELS[l]?.name ?? l.replace(/_/g, " ")} aria-label={LABELS[l]?.name ?? l.replace(/_/g, " ")}>
          {LABELS[l] ? <Icon name={LABELS[l].icon} size={16} /> : l.replace(/_/g, " ")}
        </span>
      ))}
    </span>
  );
}

// ─── Icons: MingCute's, in icons.tsx ───

export { Icon };

export function SocialLinks({ socials }: { socials: Socials }) {
  const links = (["website", "twitter", "telegram", "discord"] as const).filter((k) => socials[k]);
  if (!links.length) return null;
  const names = { website: "Website", twitter: "X", telegram: "Telegram", discord: "Discord" };
  return (
    <span className="socials">
      {links.map((k) => (
        <Out key={k} href={socials[k]!} className="icon-link" title={names[k]}>
          <Icon name={k} size={15} />
        </Out>
      ))}
    </span>
  );
}

export function Copy({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="icon-button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        });
      }}
    >
      <Icon name={done ? "check" : "copy"} size={15} />
    </button>
  );
}

// The Aurelia mark, a moon jellyfish, as the portal and the landing page draw it (ui/Logo.tsx).
const MARK =
  "M 572.5 1 C 559.8 1.4, 547.4 2, 545 2.3 C 542.5 2.7, 533.8 3.9, 525.6 5 C 517.5 6.1, 508.9 7.4, 506.6 7.9 C 504.3 8.4, 499.8 9.1, 496.5 9.4 C 473.3 12.1, 425.6 24.8, 400 35.1 C 395.8 36.8, 384.6 41.3, 375 45.1 C 332.1 62.1, 308.7 74.2, 283.1 92.5 C 279.7 95, 269.3 102.1, 260.1 108.3 C 215.5 138.4, 167.2 182.2, 139.7 217.5 C 137.1 220.8, 132.3 226.8, 129 231 C 125.6 235.1, 121.2 240.9, 119.1 244 C 117 247, 111.5 254.6, 107 261 C 93.8 279.1, 85.1 292.7, 80 303.2 C 77.3 308.5, 73.8 314.6, 72.2 316.7 C 68.2 321.7, 59.4 339.5, 52.1 357.2 C 48.8 365, 44.5 375.1, 42.4 379.5 C 40.4 383.9, 37.1 392.4, 35.3 398.5 C 26.2 427.8, 18.6 454.7, 17 463.8 C 16.4 467.2, 15.2 471.7, 14.5 473.9 C 12 480.9, 8.9 496.8, 7.4 510.6 C 6.6 518.1, 5.7 524.6, 5.4 525.1 C 4.2 526.9, 2 551.1, 2 561.5 C 2 567.5, 1.4 575.9, 0.8 580 C -0.4 588.7, 0 591.6, 3.4 595.7 C 5.8 598.5, 17.3 604.8, 27.8 608.9 C 30.3 609.9, 35.6 612.2, 39.5 614 C 46.1 617.1, 53.2 619.5, 96 633.8 C 151.2 652.2, 216.8 663.5, 283.3 665.9 C 298 666.4, 310.6 666.6, 311.3 666.4 C 311.9 666.2, 322.4 665.6, 334.5 665 C 371.1 663.2, 419.7 657, 448.5 650.3 C 464.1 646.7, 515 630.3, 530.7 623.9 C 536.1 621.6, 548.8 616.5, 559 612.5 C 569.1 608.4, 582 603.2, 587.5 600.9 C 593 598.6, 605.7 593.9, 615.9 590.3 C 626 586.7, 639.3 581.8, 645.4 579.5 C 708.6 554.8, 743.3 544.2, 782.5 537.6 C 866 523.4, 950.7 523.5, 1027 537.9 C 1044.2 541.2, 1079 549.9, 1087.7 553.1 C 1090.8 554.3, 1096.3 556, 1099.9 557 C 1103.5 557.9, 1110.7 560.3, 1116 562.2 C 1121.2 564.1, 1131.1 567.7, 1138.1 570.2 C 1145 572.7, 1154.7 576.7, 1159.6 579 C 1164.5 581.3, 1171.2 584.3, 1174.5 585.7 C 1177.8 587, 1183.6 589.9, 1187.5 592.1 C 1196.6 597, 1198.5 597, 1200.5 592.2 C 1201.9 588.9, 1202 586.9, 1201 576.5 C 1200.4 569.9, 1199.5 558.6, 1198.9 551.5 C 1198.4 544.3, 1197.5 536, 1196.9 533 C 1196.4 529.9, 1195.1 520.5, 1193.9 512 C 1184.9 444.4, 1161.6 371.3, 1133.1 321 C 1130.4 316.2, 1120.3 297.7, 1116.7 291 C 1107.3 273.4, 1076.7 231, 1060.5 213.2 C 1055.5 207.8, 1049.1 200.7, 1046.3 197.4 C 1038.2 188.1, 1018.1 167.9, 1011 161.9 C 1007.4 158.9, 1002 154.2, 999 151.4 C 965.5 120.7, 914 85.4, 875 66.2 C 859.8 58.8, 853.7 56, 826 44.1 C 789 28.3, 762.1 19.5, 736 14.5 C 728.5 13.1, 717.5 11, 711.5 9.9 C 690 5.7, 662.2 2.5, 634.5 1 C 614.5 -0, 609.5 -0, 572.5 1 M 842.5 586.1 C 799.9 591.3, 766.1 598.3, 763.3 602.5 C 761 606, 761.8 1323.2, 764.1 1330.6 C 771.9 1355.4, 796.4 1371, 821.6 1367.1 C 841.9 1364, 859 1348.2, 864.5 1327.5 C 866.6 1319.7, 866.9 588.8, 864.9 586.3 C 863.6 584.8, 854 584.6, 842.5 586.1 M 975.2 589.2 C 974.2 590.1, 974 650.9, 974 856.9 C 974 1112.4, 974.1 1123.7, 975.8 1130.5 C 982.5 1155.8, 1001.6 1171, 1027.1 1171 C 1049.2 1171, 1068.6 1155.8, 1075.7 1133 C 1078.3 1124.9, 1078.4 611, 1075.9 608.5 C 1072.3 604.9, 1021.4 593.1, 993.8 589.5 C 980 587.6, 976.7 587.6, 975.2 589.2 M 635.5 642.9 C 628.9 645.5, 617.8 649.8, 611 652.4 C 571 667.7, 553.5 675.3, 551 678.4 C 548.6 681.3, 549 1331.2, 551.3 1341.2 C 559 1373.4, 592 1390.4, 622.5 1377.6 C 636.6 1371.7, 647.3 1359.9, 652.2 1344.6 C 654.2 1338.5, 654.2 1335.8, 654 989.5 C 653.8 750.3, 653.5 640.1, 652.7 639.2 C 651.1 637.2, 648.2 637.8, 635.5 642.9 M 127.3 704.1 C 125.9 705.8, 124.7 1114.8, 126.1 1126 C 127.8 1140.2, 137.9 1156.1, 149.7 1163.2 C 179.5 1180.9, 215.7 1166.9, 227.2 1133.2 L 229.5 1126.5 229.7 925.5 C 229.9 814.9, 229.8 723.5, 229.5 722.2 C 228.8 719.5, 225.2 718.4, 211.5 716.9 C 196 715.2, 172.1 711.1, 154.5 707.3 C 134.8 702.9, 128.8 702.2, 127.3 704.1 M 422.5 712 C 416.4 713.1, 404 714.8, 395 716 C 385.9 717.1, 377.8 718.2, 377 718.5 C 376.1 718.7, 368 719.6, 358.9 720.3 C 349.7 721.1, 341.3 722.2, 340.1 722.9 L 338 724 337.7 1020.2 C 337.5 1243.3, 337.7 1318.3, 338.6 1323.7 C 346.6 1373.1, 411.5 1384.8, 435.3 1341.1 C 441.9 1328.8, 441.4 1357.4, 441 1018.5 C 440.8 816.9, 440.4 712, 439.7 711.2 C 438.4 709.6, 435.9 709.7, 422.5 712";
